import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getApiCall, postApiCall } from '../../utils/Apicalls';
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import SpinnerShow from '../../components/Spinner';
import { Payment as PaymentIcon } from '@mui/icons-material';
import './Checkout.css';

function CheckoutPage() {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [quantity, setQuantity] = useState(3);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const itemId = useParams();
  const [searchParams] = useSearchParams();

  const [orderDetails, setOrderDetails] = useState({
    priceUnit: 0,
    subtotal: 0,
    processingFee: 0,
    total: 0
  });

  // Calculate processing fee (5.9% + $0.30)
  const calculateProcessingFee = (subtotal) => {
    return (subtotal * 0.059) + 0.30;
  };

  // Calculate total with processing fee
  const calculateTotal = (subtotal) => {
    const processingFee = calculateProcessingFee(subtotal);
    return subtotal + processingFee;
  };

  useEffect(() => {
    // Check if user is logged in
    // const userDetails = localStorage.getItem("userDetails");
    // if (!userDetails) {
    //   navigate('/login', { state: { from: location.pathname } });
    //   return;
    // }

    // Fetch available plans
    const fetchPlans = async () => {
      try {
        const result = await getApiCall('interview-app/plans/list');
        if (result?.status && result?.result) {
          if (itemId?.id) {
            const plan = result.result.find(plan => plan.id === itemId.id);
            if (plan) {
              setSelectedPlan(plan);
              setPlans([plan]);
            }
            return;
          }
          // Filter out free plans
          const paidPlans = result.result.filter(plan => plan.price !== "0");
          setPlans(paidPlans);
          
          // Set default selected plan (first non-free plan)
          if (paidPlans.length > 0) {
            setSelectedPlan(paidPlans[0]);
          }
        } else {
          setError("Failed to load plans data");
        }
      } catch (err) {
        console.error("Error fetching plans:", err);
        setError("An error occurred while fetching plans. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [navigate, location.pathname]);

  useEffect(() => {
    // Calculate subtotal
    calculatePrice(quantity);
  }, [quantity, selectedPlan]);

  const calculatePrice = (quantityVal) => {
    let subtotal = 0;
    let priceUnit = 0;
    if (selectedPlan?.id === "9b1deb4d-9bdd-3b7d-4bad-2b0d7b3dcb1d") {
      priceUnit = selectedPlan.price;
      if (quantityVal > 0 && quantityVal <= 2) {
        priceUnit = 14.99;
      } else if (quantityVal > 2 && quantityVal <= 5) {
        priceUnit = 12.99;
      } else if (quantityVal > 5 && quantityVal <= 15) {
        priceUnit = 11.99;
      } else if (quantityVal > 15) {
        priceUnit = 9.99;
      }
      subtotal = parseFloat(priceUnit) * quantityVal;
    } else if (selectedPlan) {
      priceUnit = selectedPlan.price;
      subtotal = parseFloat(selectedPlan.price) * quantityVal;
    }
    // Calculate processing fee
    const processingFee = calculateProcessingFee(subtotal);
    // Calculate total with processing fee
    const total = calculateTotal(subtotal);

    setOrderDetails({
      priceUnit,
      subtotal: subtotal,
      processingFee,
      total
    });
  }

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    if (plan?.id === "9b1deb4d-9bdd-3b7d-4bad-2b0d7b3dcb0d") {
      setQuantity(15);
    }
    // Store plan details in localStorage for the success page
    localStorage.setItem('currentPlanName', plan.name);
    localStorage.setItem('currentPlanPrice', orderDetails.priceUnit);
    localStorage.setItem('currentPlanTime', plan.session_time);
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0 && value <= 20) {
      setQuantity(value);
    }
  };

  const changeValume = (quantity) => {
    if (quantity > 0 && quantity <= 20) {
      setQuantity(quantity);
      calculatePrice(quantity);
    }
  }

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      localStorage.removeItem('email');
      // Call backend to create purchase plan
      let startPayload = {
        planId: selectedPlan.id,
        purchaseHours: quantity,
        processingFee: Number(calculateProcessingFee(orderDetails.subtotal).toFixed(2))
      };
      const email = searchParams.get("email");
      if (email) {
        startPayload.email = email;
        localStorage.setItem('email', email);
      }
      console.info("Checkout session started:"); 
      const result = await postApiCall('interview-app/purchaseplan/start', startPayload);
      console.info("Checkout session result:", result); 
      if (result?.status && result?.result) {
        // Store the purchase ID in localStorage for verification later
        if (result.result.inserted) {
          localStorage.setItem('currentPurchaseId', result.result.inserted);
        }
        
        // Redirect to Stripe checkout URL
        if (result.result.url) {
          window.location.href = result.result.url;
        } else {
          setError("No checkout URL returned from server");
          setProcessing(false);
        }
      } else {
        setError(result?.message || "Failed to create checkout session");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError("An error occurred while processing your request. Please try again later.");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="loading-container">
          <SpinnerShow />
        </div>
        <Footer />
      </>
    );
  }


  return (
    <>
      <Header />
      <div className="checkout-container">
        <div className="checkout-header">
          <h1 className='text-white'>Complete Your Purchase</h1>
          <p style={{ color: 'white' }}>Select a plan and quantity to continue with your interview sessions</p>
        </div>
        
        {error && (
          <div className="checkout-error">
            <p>{error}</p>
          </div>
        )}
        
        <div className="checkout-content">
          <div className="plan-selection">
            <h2>Select Your Plan</h2>
            <div className="plans-grid">
              {plans.map((plan) => (
                <div 
                  key={plan.id} 
                  className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                  onClick={() => handlePlanSelect(plan)}
                >
                  <div className="plan-header">
                    <h3 className='text-dark'>{plan.name}</h3>
                    <div className="plan-price">
                      <span className="currency">$</span>
                      <span className="amount">{plan.price}</span>
                    </div>
                  </div>
                  <div className="plan-details">
                    {/* <p className='text-dark'>{plan.session_time} {parseInt(plan.session_time) === 1 ? "minute" : "minutes"} of interview time</p> */}
                    <ul className="plan-features">
                      {plan.description.map((feature, index) => (
                        <li className='text-dark' key={index}>{feature.name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="order-summary">
            <h2>Order Summary</h2>
            {selectedPlan ? (
              <>
                <div className="summary-item">
                  <span className='text-dark'>Plan:</span>
                  <span className='text-dark'>{selectedPlan.name}</span>
                </div>
                <div className="summary-item">
                  <span className='text-dark'>Price per unit:</span>
                  <span className='text-dark'>${orderDetails.priceUnit}</span>
                </div>
                {selectedPlan.id === "9b1deb4d-9bdd-3b7d-4bad-2b0d7b3dcb1d" && (
                  <div className="summary-item quantity-selector">
                    <span className='text-dark'>Quantity:</span>
                    <div className="quantity-controls">
                      <button 
                        type="button" 
                        onClick={() => quantity > 1 && changeValume(quantity - 1)}
                        disabled={quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        -
                      </button>
                      <input 
                        type="number" 
                        min="3" 
                        max="20" 
                        value={quantity} 
                        readOnly={true}
                        // onChange={handleQuantityChange} 
                        aria-label="Quantity"
                      />
                      <button 
                        type="button" 
                        onClick={() => quantity < 20 && changeValume(quantity + 1)}
                        disabled={quantity >= 20}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                <div className="summary-item">
                  <span className='text-dark'>Subtotal:</span>
                  <span className='text-dark'>${orderDetails.subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-item">
                  <span className='text-dark'>Processing Fee (5.9% + $0.30):</span>
                  <span className='text-dark'>${orderDetails.processingFee.toFixed(2)}</span>
                </div>
                <div className="summary-item total">
                  <span className='text-dark'>Total:</span>
                  <span className='text-dark'>${orderDetails.total.toFixed(2)}</span>
                </div>
                <button 
                  className="checkout-button" 
                  onClick={handleCheckout}
                  disabled={processing}
                >
                  {processing ? 'Processing...' : (
                    <>
                      <PaymentIcon className="payment-icon" />
                      <span>Proceed to Payment</span>
                    </>
                  )}
                </button>
                <div className="payment-info">
                  <p>You'll be redirected to Stripe's secure payment page</p>
                  {/* <div className="payment-methods">
                    <img src="/images/visa.svg" alt="Visa" />
                    <img src="/images/mastercard.svg" alt="Mastercard" />
                    <img src="/images/amex.svg" alt="American Express" />
                  </div> */}
                </div>
              </>
            ) : (
              <p>Please select a plan to continue</p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default CheckoutPage;




